import type { ToggleOption } from "@replo/design-system/components/toggle/ToggleGroup";
import type { ContainerAlignmentModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useSelectedElement } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import {
  AlignCenterVertical,
  AlignEndVertical,
  AlignStartVertical,
} from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

interface ContainerAlignmentModifierProps {
  onChange: (modifiers: ContainerAlignmentModifierValue[]) => void;
  disabled?: boolean;
}

const ContainerAlignmentModifierComponent: React.FC<
  ContainerAlignmentModifierProps
> = ({ onChange, disabled = false }) => {
  const selectedElement = useSelectedElement();

  const justifyContent = selectedElement?.styleOverrides.justifyContent;
  // TODO (Kurt, 2025-07-24): We are going to have to find a way to deal with justify
  // classes that we don't support (so if the LLM uses a justify class that we don't
  // support we need a way to communicate that with the user, will talk with jasmine
  // about this)
  const currentValue = (() => {
    switch (justifyContent) {
      case "flex-start":
        return "start";
      case "center":
        return "center";
      case "flex-end":
        return "end";
      default:
        return null;
    }
  })();

  const [alignment, setAlignment] = useOverridableState<string>(
    currentValue ?? "",
    (newAlignment: string) => {
      onChange([
        {
          type: "containerAlignment" as const,
          value: newAlignment as ContainerAlignmentModifierValue["value"],
        },
      ]);
    },
  );

  const alignmentOptions: ToggleOption[] = [
    {
      value: "start",
      label: <AlignStartVertical size={16} />,
      tooltipContent: "Align Start",
    },
    {
      value: "center",
      label: <AlignCenterVertical size={16} />,
      tooltipContent: "Align Center",
    },
    {
      value: "end",
      label: <AlignEndVertical size={16} />,
      tooltipContent: "Align End",
    },
  ];

  return (
    <div className="container-alignment-modifier">
      <ToggleGroup
        size="sm"
        selectedValue={alignment}
        options={alignmentOptions}
        onChange={setAlignment}
        disabled={disabled}
        layoutClassName="w-full"
      />
    </div>
  );
};

export default ContainerAlignmentModifierComponent;
